import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

export function useQuery(): URLSearchParams {
    const { search } = useLocation() as { search: string };
    return React.useMemo<URLSearchParams>(() => new URLSearchParams(search), [search]);
}

export function usePage(): number {
    const { page: PageParam } = useParams();
    let page = Number(PageParam);
    if (page < 1 || !page) page = 1;
    return page;
}

export function useNumberParam(name: string): number | undefined {
    const params = useParams<{ [name]?: string }>();
    return params[name] ? Number(params[name]) : undefined;
}

export function useDateParams(): { year: number; month: number } {
    const { year: YearParam, month: MonthParam } = useParams();

    const now = new Date();
    let year = Number(YearParam);
    if (!year) year = now.getFullYear();

    let month = Number(MonthParam);
    if (!month) {
        month = now.getMonth() + 1;
    }

    return {
        year: Number(year),
        month: Number(month),
    };
}

function parseFilters(
    query: URLSearchParams,
    parsers: Record<string, (elem: string) => any>,
): Record<string, any> {
    const filters: Record<string, any> = {};
    query.forEach((v: string, k: string) => {
        let decoded: string;

        // this covers the case of invalid or incomplete escape sequence: %, %A, and so.
        // if it fails, we just set the value as is.
        try {
            decoded = decodeURIComponent(v);
        } catch (e) {
            decoded = v;
            console.warn(e);
        }

        const val = decoded;
        if (parsers[k]) {
            filters[k] = parsers[k](decoded);
        } else if (decoded[0] === '{' || decoded[0] === '[') {
            // we can try to check if the brackets are balanced but requires more js.
            // so we just try to parse it and catch the error
            // if it fails, we just set the value as is.
            try {
                filters[k] = JSON.parse(decoded);
            } catch (e) {
                console.warn(e);
                filters[k] = val;
            }
        } else if (!isNaN(Number(decoded))) {
            filters[k] = Number(decoded);
        } else {
            filters[k] = val;
        }
    });

    return filters;
}

function buildSearch(filters: Record<string, any>): string {
    const parts = [];
    for (const k of Object.keys(filters)) {
        const v = filters[k];
        if (!v) continue;

        let val;
        switch (typeof v) {
            case 'string':
            case 'number':
                val = v;
                break;
            case 'object':
                val = JSON.stringify(v);
                break;
            default:
                val = `${v}`;
        }
        parts.push(`${k}=${encodeURIComponent(val)}`);
    }
    return '?' + parts.join('&');
}

export type QueryFilters<T extends Record<string, any>> = T & {
    setFilters: (filters?: Partial<T>, replace?: boolean) => void;
};
export function useQueryFilters<T extends Record<string, any>>(
    defaultFilters: T = {} as T,
    parsers = {},
    resetPagination = true,
): QueryFilters<T> {
    const location = useLocation();
    const navigate = useNavigate();
    const query = useQuery();

    const [state, setState] = useState({
        search: location.search,
        filters: {
            ...defaultFilters,
            ...parseFilters(query, parsers),
        },
    });

    useEffect(() => {
        if (location.search !== state.search) {
            setState({
                search: location.search,
                filters: {
                    ...defaultFilters,
                    ...parseFilters(query, parsers),
                },
            });
        }
    }, [location.search]);

    const updateUrl = (search: string, replace: boolean) => {
        let path = location.pathname;
        if (resetPagination && path.includes('/p/')) {
            path = path.split('/p/')[0];
        }
        void navigate(`${path}${search}`, { replace });
    };

    const handleFiltersChange = (filters = {}, replace = false) => {
        const newFilters = { ...state.filters, ...filters };
        const search = buildSearch(newFilters);
        if (search !== state.search) {
            updateUrl(search, replace);
        }
        setState({ search, filters: newFilters });
    };

    return {
        ...state.filters,
        setFilters: handleFiltersChange,
    };
}

const RETURN_URL_KEY = 'return_to';

// returns current location (pathname + search + hash) as return_to query string
export function useLocationAsReturnTo(): string {
    const location = useLocation();
    const query = new URLSearchParams();
    query.append(
        RETURN_URL_KEY,
        `${location.pathname ?? ''}${location.search ?? ''}${location.hash ?? ''}`,
    );
    return query.toString();
}

// returns the url from return_to in the query string
export function useReturnTo(): string {
    const query = useQuery();
    return query.get(RETURN_URL_KEY) ?? '';
}

// returns a function that navigates to the return_to in the query string
export function useNavigateToReturnTo(): () => void {
    const navigate = useNavigate();
    const url = useReturnTo();
    return () => {
        void navigate(url);
    };
}
